<template>
  <card :title="computedTitle" :on-cancel="cancel">
    <p v-if="!editMode" class="lookup-intro d-flex align-items-center">
      <i class="material-icons me-2">info</i>
      <span>{{ $t('endCustomers.lookupIntro') }}</span>
    </p>
    <Form ref="form" v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(saveEndCustomer)">
        <div class="row mb-3">
          <div class="col-md-4">
            <material-input-field
              id="identificationNumberInput"
              ref="identificationNumberInput"
              v-model="identificationNumber"
              :name="'identificationNumber'"
              variant="static"
              :disabled="identificationNumberLocked"
              :label="$t('endCustomers.identificationNumber') + (editMode ? '' : ' *')"
              :placeholder="$t('endCustomers.identificationNumberPlaceholder')"
              maxlength="11"
            />

            <div v-if="!identificationNumberLocked" class="registry-status mt-1">
              <small v-if="lookupState === 'searching'" class="text-secondary d-flex align-items-center">
                <i class="material-icons spin me-1">progress_activity</i>
                {{ $t('endCustomers.searchingRegistry') }}
              </small>
              <small v-else-if="lookupState === 'found'" class="text-success d-flex align-items-center">
                <i class="material-icons me-1">check_circle</i>
                <span>
                  <strong>{{ registryEntry.name }}</strong>
                  <template v-if="registryEntry.address"> — {{ registryEntry.address }}</template>
                  <template v-if="registryEntry.status"> ({{ registryEntry.status }})</template>
                </span>
              </small>
              <small v-else-if="lookupState === 'inactive'" class="text-danger d-flex align-items-center">
                <i class="material-icons me-1">block</i>
                <span>
                  <strong>{{ registryEntry.name }}</strong> ({{ registryEntry.status }}) — {{ $t('endCustomers.inactiveInRegistry') }}
                </span>
              </small>
              <small v-else-if="lookupState === 'notFound'" class="text-danger d-flex align-items-center">
                <i class="material-icons me-1">error</i>
                {{ $t('endCustomers.notFoundInRegistry') }}
              </small>
              <small v-else-if="lookupState === 'failed'" class="text-warning d-flex align-items-center">
                <i class="material-icons me-1">warning</i>
                {{ $t('endCustomers.registryLookupFailed') }}
              </small>
              <small v-else-if="!editMode" class="text-secondary">
                {{ $t('endCustomers.enterKennitalaHint') }}
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <material-input-field
              id="endCustomerNameInput"
              v-model="name"
              :name="'name'"
              variant="static"
              :disabled="detailsLocked"
              :label="$t('common.name') + ' *'"
              :placeholder="$t('common.name')"
            />
          </div>

          <div class="col-md-4">
            <material-input-field
              id="endCustomerAddressInput"
              v-model="address"
              :name="'address'"
              variant="static"
              :disabled="detailsLocked"
              :label="$t('endCustomers.address')"
              :placeholder="$t('endCustomers.address')"
            />
          </div>

          <div class="col-md-4">
            <material-input-field
              id="endCustomerEmailInput"
              v-model="email"
              :name="'email'"
              type="email"
              variant="static"
              :disabled="detailsLocked"
              :error="emailInvalid"
              :label="$t('common.email') + ' *'"
              :placeholder="$t('common.email')"
            />
            <small v-if="emailInvalid" class="text-danger">{{ $t('errors.invalidEmail') }}</small>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-sm d-flex justify-content-end">
            <material-button variant="gradient" color="dark" :disabled="!canSubmit" @click="saveEndCustomer">
              {{ $t('common.submit') }}
            </material-button>
          </div>
        </div>
      </form>
    </Form>
  </card>
</template>
<script>
import api from "../../services/api";
import { Form } from 'vee-validate';
import Card from "../../components/Card.vue";
import MaterialInputField from "@/components/MaterialInputField.vue";
import MaterialButton from "@/components/MaterialButton.vue";

export default {
  components: {
    Form,
    Card,
    MaterialInputField,
    MaterialButton
  },
  props: {
    editMode: {
      type: Boolean,
      required: true
    },
    // When true (ERP synced tenant) the kennitala must be verified against the
    // Icelandic company/national registry before the customer can be created
    requireRegistryVerification: {
      type: Boolean,
      required: false,
      default: false
    },
    editingEndCustomerId: {
      type: Number,
      required: false,
      default: null
    },
    nameProp: {
      type: String,
      required: false,
      default: ''
    },
    addressProp: {
      type: String,
      required: false,
      default: ''
    },
    emailProp: {
      type: String,
      required: false,
      default: ''
    },
    identificationNumberProp: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['close', 'refresh-data'],
  data() {
    return {
      name: this.nameProp,
      address: this.addressProp,
      email: this.emailProp,
      identificationNumber: this.identificationNumberProp,
      lookupState: '', // '', 'searching', 'found', 'inactive', 'notFound', 'failed'
      registryEntry: {},
      lookupTimeout: null,
      saving: false
    };
  },
  computed: {
    computedTitle() {
      return this.editMode ? this.$t('endCustomers.editEndCustomer') : this.$t('endCustomers.createEndCustomer');
    },
    cleanedEmail() {
      return (this.email || '').trim();
    },
    emailInvalid() {
      return this.cleanedEmail !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.cleanedEmail);
    },
    cleanedIdentificationNumber() {
      return (this.identificationNumber || '').replace(/-/g, '').trim();
    },
    identificationNumberLocked() {
      // The kennitala is the sync key against the ERP — it cannot change once created
      return this.editMode && this.requireRegistryVerification;
    },
    detailsLocked() {
      // Creating: name/address stay locked until the kennitala is verified, then autofill
      return !this.editMode && this.lookupState !== 'found';
    },
    canSubmit() {
      if (this.saving) return false;
      if (this.name === '' || this.name === null) return false;
      // Required here even though synced ERP customers may lack one
      if (this.cleanedEmail === '' || this.emailInvalid) return false;
      if (!this.editMode) {
        return this.lookupState === 'found';
      }
      return true;
    }
  },
  watch: {
    identificationNumber() {
      if (this.identificationNumberLocked) return;
      this.lookupState = '';
      this.registryEntry = {};
      clearTimeout(this.lookupTimeout);
      if (/^\d{10}$/.test(this.cleanedIdentificationNumber)) {
        this.lookupTimeout = setTimeout(this.lookupRegistry, 350);
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.lookupTimeout);
  },
  methods: {
    lookupRegistry() {
      const nationalId = this.cleanedIdentificationNumber;
      this.lookupState = 'searching';
      api.get(`/EndCustomer/NationalRegistry/${nationalId}`)
        .then(response => {
          // Ignore stale responses if the user kept typing
          if (nationalId !== this.cleanedIdentificationNumber) return;
          this.registryEntry = response.data;
          if (response.data.inactive) {
            this.lookupState = 'inactive';
            return;
          }
          this.lookupState = 'found';
          this.name = response.data.name;
          if (response.data.address) {
            this.address = response.data.address;
          }
        })
        .catch(error => {
          if (nationalId !== this.cleanedIdentificationNumber) return;
          if (error.response && error.response.status === 404) {
            this.lookupState = 'notFound';
          } else {
            console.error('API request error:', error); // eslint-disable-line no-console
            this.lookupState = 'failed';
          }
        });
    },
    saveEndCustomer() {
      if (!this.canSubmit) return;
      this.saving = true;

      const body = {
        name: this.name,
        address: this.address === '' ? null : this.address,
        email: this.cleanedEmail,
        identificationNumber: this.cleanedIdentificationNumber === '' ? null : this.cleanedIdentificationNumber
      };

      const request = this.editMode
        ? api.put(`/EndCustomer/${this.editingEndCustomerId}`, body)
        : api.post('/EndCustomer', body);

      request
        .then(() => {
          this.$toast.success(
            this.editMode ? this.$t('endCustomers.endCustomerUpdated') : this.$t('endCustomers.endCustomerCreated'),
            { timeout: 2000 }
          );
          this.$emit('refresh-data');
          this.$emit('close');
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          const data = error.response?.data;
          const message = data?.detail
            || data?.title
            || (Array.isArray(data) && data[0]?.errorMessage)
            || this.$t('common.somethingWentWrong');
          this.$toast.danger(message, { timeout: 0 });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    cancel() {
      this.$emit('close');
    }
  }
};
</script>
<style scoped>
.lookup-intro {
  font-size: 0.875rem;
  color: #7b809a;
  background: rgba(26, 115, 232, 0.08);
  border-radius: 0.5rem;
  padding: 0.6rem 0.9rem;
  margin-bottom: 1rem;
}
.lookup-intro .material-icons {
  font-size: 18px;
  color: #1a73e8;
}
.registry-status .material-icons {
  font-size: 16px;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
